// UI-independent helpers. Domain rules stay in the backend; this file only shapes data for display.

/** `energy.meter.bidirectional@1` → `energy.meter.bidirectional` (translation keys are per id, not per version). */
export function capabilityId(ref: string): string {
  const at = ref.lastIndexOf("@");
  return at > 0 ? ref.slice(0, at) : ref;
}

/**
 * Mirrors the host's "operate" policy so the UI can disable actions up front.
 * The host still checks every request; this only avoids offering buttons that would fail.
 */
export function canOperate(roles: readonly string[]): boolean {
  return roles.includes("admin") || roles.includes("operator");
}

export interface ValueLike {
  channel: string;
  attribute: string;
  unit: string;
  number: number | null;
  text: string | null;
}

/** False for a gap: a value the device did not measure or does not have (shown nowhere, never as zero). */
export function hasValue(v: ValueLike): boolean {
  return v.number !== null || v.text !== null;
}

const CHANNEL_ORDER = (channel: string) => (channel === "main" ? "" : channel);

/** Groups current values by template channel ("main" first, then phase1…3), attributes alphabetically. */
export function groupByChannel<T extends ValueLike>(
  values: readonly T[],
): { channel: string; values: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const v of values) {
    groups.set(v.channel, [...(groups.get(v.channel) ?? []), v]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => CHANNEL_ORDER(a).localeCompare(CHANNEL_ORDER(b)))
    .map(([channel, list]) => ({
      channel,
      values: [...list].sort((a, b) => a.attribute.localeCompare(b.attribute)),
    }));
}

/** Number with locale formatting and a translated unit symbol; a gap (null) shows as a dash, never as zero. */
export function formatValue(
  v: ValueLike,
  locale: string,
  translate: (key: string, fallback: string) => string,
): string {
  if (v.number === null) {
    return v.text === null ? "–" : translate(`enum.${v.text}`, v.text);
  }
  const digits =
    Math.abs(v.number) >= 1000 ? 1 : Math.abs(v.number) >= 10 ? 2 : 3;
  const unit = translate(`unit.${v.unit}`, v.unit);
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(v.number)} ${unit}`.trim();
}
